/**
 * MIRRORED FILE — twin: src/utils/unitConversionGraph.js
 * Change one, change both. `mirroredUtilParity.test.js` fails the build otherwise.
 *
 * Do an item's stated conversions AGREE with each other, and what is the ratio
 * between any two units they connect?
 *
 *     1 CASE = 10 PCS          ─┐
 *     1 PCS  = 20 KG            ├─ therefore 1 CASE = 200 KG, and only 200
 *     1 CASE = 210 KG   ← this ─┘  is refused, with 200 named in the refusal
 *
 * ── Why this exists ───────────────────────────────────────────────────────────
 * `unitConversions.ts` owns which SINGLE row feeds each of the two legacy
 * columns (`units_per_case`, `unit_size`) — first-of-each-role, everything else
 * recorded and read by nothing. That left two holes this module fills:
 *
 *   - Nothing checked the rows against each other. A person could state a pack,
 *     a size, and a third row contradicting the product of the first two, and
 *     all three saved. The QA row "ironman" is exactly that shape.
 *   - A price could only ever be derived at the four fixed tiers the two columns
 *     describe. A unit the item genuinely states a path to — PCS on a
 *     CASE-priced row two hops away — had no way to be priced at all.
 *
 * So: nodes are canonical unit names, each stored row is a bidirectional edge,
 * and both questions are one breadth-first search over that graph.
 *
 * ── This is NOT the chaining that caused QUO-00011 ────────────────────────────
 * `packConversion.ts`'s header documents a ratcheting bug: each conversion hop
 * was computed from the PREVIOUS HOP'S ALREADY-ROUNDED OUTPUT, so a round trip
 * gained a cent per hop. Its fix was to reduce everything to one hub and always
 * compute from the caller's anchor.
 *
 * The traversal here is the same discipline, not the bug it replaced. Every walk
 * starts from the RAW STORED ROWS and multiplies their exact ratios; nothing is
 * rounded until the single comparison or the single price at the very end, and
 * no derived output is ever fed back in as an input. `reachableUnits` exists so
 * that pricing every unit at once is ONE walk rather than N walks that would
 * each re-derive a shared prefix.
 *
 * ── Self-loop rows are not edges ──────────────────────────────────────────────
 * "1 CASE = 6 CASE" on a CASE-counted item is a real, stored, meaningful row:
 * the outer pack happens to share a name with the item's own unit, and
 * `legacyPackFields` reads it as the pack ratio (TECH_DEBT #155's shape). It is
 * NOT a claim that a unit equals a multiple of itself. Treated as an edge it
 * would make every unit reachable from every other at a bogus factor of 6, so
 * it is skipped everywhere in this file — and skipped by `checkRowAgainstGraph`
 * too, since there is nothing to contradict. Do not "fix" this into an edge.
 */

import {
  normalizeConversion,
  normalizeConversions,
  conversionRatio,
  formatQty,
  UnitConversion,
} from './unitConversions';
// `isWholeCountUnit` joins `canonicalUnit` here for the whole-number predicate
// below — see that predicate's own header for why the dependency runs this
// direction and not the other.
import { canonicalUnit, isWholeCountUnit } from './unitVocabulary';
import {
  KG_PER_UNIT,
  LITRE_PER_UNIT,
  METRE_PER_UNIT,
  ounceLitres,
  weighsButNotOunce,
} from './packConversion';

/** One hop of a path through the graph. */
export type ConversionStep = { fromUnit: string; toUnit: string; ratio: number };

/** A ratio the graph derived, with the path and the rows that produced it. */
export type DerivedRatio = { ratio: number; steps: ConversionStep[]; rows: UnitConversion[] };

/** One node's outgoing edges: neighbour -> how many neighbour in one of this. */
export type ConversionGraph = Map<string, Map<string, { ratio: number; row: UnitConversion }>>;

/** A pricing walk crosses the ounce to volume, at the item's litres per ounce (D170.4). */
export type OunceOptions = { ounceByWater?: boolean; litrePerOunce?: number | null };

/** A row agrees with the graph, or it does not and this says how. */
export type RowVerdict =
  | { ok: true; redundant: boolean }
  | {
      ok: false;
      fromQty: number;
      fromUnit: string;
      toUnit: string;
      statedToQty: number;
      expectedToQty: number;
      path: string;
      message: string;
      index?: number;
    };

/**
 * How many conversion rows one item may hold.
 *
 * Lived as a private constant in `masterCatalogue.controller.js` while the item
 * form was the only writer. The vendor sync now appends rows too, and a cap only
 * one of two writers enforces is not a cap — so it lives with the rules the
 * writers share.
 */
export const MAX_UNIT_CONVERSIONS = 20;

/**
 * Six decimals — the numeric sibling of `unitConversions.formatQty`'s string
 * convention, deliberately the same precision so a value this module calls equal
 * and a value that module prints cannot disagree.
 *
 * This IS the agreed equality test: two paths between the same pair of units
 * must land on the same number to six decimals, with no tolerance band beyond
 * it. Six decimals is fine enough to hold any pack ratio this system stores and
 * coarse enough to erase the float dust a chain of divisions leaves behind
 * (200.00000000000003 and 200 are one number here).
 *
 * It is the QUANTIZER, not the comparison. `quantitiesAgree` below is what
 * `checkRowAgainstGraph` asks, because this scaling has a ceiling and the
 * question "do these two agree" must not. `round6` itself keeps its exact
 * behaviour at every magnitude — it still shapes the number a REFUSAL names, and
 * changing that would change a figure on screen (TECH_DEBT #188).
 */
export function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * The magnitude at which `round6` stops being a rounding at all.
 *
 * Above it `n * 1e6` passes 2^53, so `Math.round` has nothing left to round —
 * the product is already an integer-valued double — and `round6(a) === round6(b)`
 * silently degrades into raw float equality. A genuinely CONSISTENT chain would
 * then be reported as a conflict on the strength of dust one multiplication left
 * behind: "1 PALLET = 73.97 BOX, 1 BOX = 7.13 CARTON, 1 CARTON = 51.35 KG"
 * derives 27,082,303,235.000004 MG for a chain whose exact value is
 * 27,082,303,235, and the row stating the exact value was refused (TECH_DEBT
 * #188).
 *
 * No packaging chain this catalogue holds comes near this — milligrams in a
 * pallet is ~1.4e8, sixty times under it — which is why the guard is set HERE
 * and not lower. Below it the comparison runs the same expression it always ran,
 * on the same two numbers, so nothing the catalogue can hold moves.
 */
export const ROUND6_MAX_MAGNITUDE = Number.MAX_SAFE_INTEGER / 1e6; // 9,007,199,254.740992

/**
 * Where a value too large for the six-decimal grid is compared instead.
 *
 * Six decimals at magnitude 1e9 is a RELATIVE grid of 1e-15 — the finest a
 * double can be trusted to carry, and the exact point past which asking for six
 * decimals asks for digits the representation does not hold. So above the
 * ceiling the comparison holds that relative precision constant instead of going
 * on demanding absolute decimals: both sides are divided by the same power of
 * ten until they sit at or below 1e9, and `round6` runs there.
 *
 * This is the SCALED comparison the entry called for and deliberately not a
 * tolerance band. It is still `round6`, still an exact grid, still refusing
 * anything that lands in a different cell: at 2.7e10 it refuses a row that is
 * wrong by 0.0001. A tolerance admits near-misses; this admits only the dust the
 * double has no room to represent.
 */
const SCALED_COMPARISON_CEILING = 1e9;

/**
 * The power of ten that brings `magnitude` down onto the ceiling.
 *
 * A loop rather than `Math.pow(10, Math.ceil(Math.log10(m)) - 9)`: `Math.log10`
 * is off by an ulp at exact powers of ten often enough that the derived exponent
 * is not trustworthy, and a comparison deciding whether a row saves cannot rest
 * on that. It runs at most once per candidate row, and only above the ceiling —
 * which is to say, so far, never.
 */
function comparisonScale(magnitude: number): number {
  let scale = 1;
  while (magnitude / scale > SCALED_COMPARISON_CEILING) scale *= 10;
  return scale;
}

/**
 * Do two quantities agree, under the equality rule the registry records at
 * BUSINESS_RULES#conversions-agree-two-hops?
 *
 * At or below `ROUND6_MAX_MAGNITUDE` this is the SAME EXPRESSION it has always
 * been — `round6(a) === round6(b)`, on the same two numbers — so every chain
 * this catalogue can hold is compared exactly as it was before TECH_DEBT #188.
 * Above it, six decimals is finer than one ulp and erases nothing, so the same
 * grid is applied at the largest magnitude where it still means something.
 *
 * A non-finite operand takes the old path unchanged: `round6` already answers
 * Infinity (equal to itself) and NaN (equal to nothing), and the scale loop
 * would not terminate on either.
 */
export function quantitiesAgree(a: number, b: number): boolean {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return round6(a) === round6(b);
  const magnitude = Math.max(Math.abs(a), Math.abs(b));
  if (magnitude <= ROUND6_MAX_MAGNITUDE) return round6(a) === round6(b);
  const scale = comparisonScale(magnitude);
  return round6(a / scale) === round6(b / scale);
}

/**
 * WHOLE-NUMBER CONVERSION RULE (D64, owner 2026-09-24) — THE ONE OWNER.
 *
 * Before this it was written three times, each a slightly different question:
 *   - `quotationUnitConversions.isWholeForTarget` (client, private) —
 *     `!isWholeCountUnit(target) || quantitiesAgree(q, Math.round(q))`.
 *   - `rfq.service.applyVendorSplitToRfqItems` — the SAME expression, typed out
 *     by hand twice (once guarded by `isPackUow`, which happens to imply
 *     `isWholeCountUnit`, so the two were equivalent without saying so).
 *   - `quotationUnitConversions.isFractionalWholeCount` — the same QUESTION,
 *     but tested with `Number.isInteger`, a DIFFERENT tolerance from
 *     `quantitiesAgree`. A ratio that is whole after a chain of divisions can
 *     carry float dust `Number.isInteger` refuses and `quantitiesAgree`
 *     forgives — which is exactly the class of bug a second tolerance clone
 *     always eventually is (CLAUDE.md's golden rule; the `num()` /
 *     `DEFAULT_TT_VAT_RATE` incident is the same shape).
 *
 * ONE owner, here, because this predicate needs BOTH `isWholeCountUnit` (from
 * `unitVocabulary`) and `quantitiesAgree` (above). It does NOT live in
 * `unitVocabulary` itself: that module is the lower layer — dozens of files
 * import it for "what word is this" with no notion of a conversion graph, and
 * it must never grow a dependency on a module built on top of it (this one
 * included). `unitConversionGraph` already imports `canonicalUnit` from
 * `unitVocabulary` one-directionally; adding `isWholeCountUnit` to that same
 * import is free, where the reverse would be circular.
 *
 * `isWholeCountQuantity(quantity, unit)` is the GATE: may `quantity`, once
 * expressed in `unit`, actually be written down? A continuous unit (KG, LB,
 * L...) says yes to any finite number — 2.5 KG is an ordinary thing to sell.
 * A unit that COUNTS things (`isWholeCountUnit` — CASE, PCS, BOX, ..., and
 * BAG/DOZ/SET, owner 2026-09-24, "nobody sells 2.4 bags or 1.5 dozen") says
 * yes only to a whole number of it, to `quantitiesAgree`'s tolerance.
 *
 * Every caller of the three retired spellings now calls this (or the flag
 * below): the client's Unit dropdown and catalogue-pick restatement
 * (`quotationUnitConversions.ts`), and `rfq.service.applyVendorSplitToRfqItems`.
 */
export function isWholeCountQuantity(quantity: number, unit: unknown): boolean {
  if (!Number.isFinite(quantity)) return false;
  if (!isWholeCountUnit(unit)) return true;
  return quantitiesAgree(quantity, Math.round(quantity));
}

/**
 * The FLAG built on the same gate, for the quotation grid's Quantity cell:
 * is `quantity` — a restatement, or a person's own keystroke, indistinguishably
 * — a POSITIVE fractional count of a unit that counts things? Never a GATE
 * itself: it does not decide `status` in `quotationLineAmount.lineAmountCheck`
 * or anything `quotationConfirmationRules` reads, and Finalize/Send/Confirm do
 * not read it either (TECH_DEBT #150's round-up chip was replaced by this
 * flag, not by a new block).
 *
 * `quotationLineAmount.expectedIsWholeCount` (F5, 2026-09-25) DOES read this —
 * to decide which of D51's remedies the "use the ask" chip may offer, never to
 * move `status` or `blocksQuotation`. A UI-only reader is not a gate.
 *
 * Lenient on its input on purpose — the grid hands it whatever sits in the
 * cell: a string mid-edit, `null` on a line with no quantity, or a number.
 */
export function isFractionalWholeCount(quantity: unknown, unit: unknown): boolean {
  const qty = typeof quantity === 'number' ? quantity : parseFloat(String(quantity == null ? '' : quantity));
  if (!Number.isFinite(qty) || qty <= 0) return false;
  return !isWholeCountQuantity(qty, unit);
}

/**
 * A unit name as this module keys it, matching `normalizeConversion` exactly —
 * canonical where the vocabulary knows the word, upper-cased as typed where it
 * does not. A supplier's own name for a pack is still a node.
 */
export function canonicalUnitName(raw: unknown): string | null {
  const text = String(raw == null ? '' : raw).trim();
  if (!text) return null;
  return canonicalUnit(text) || text.toUpperCase();
}

/**
 * The conversions that are true of every item because they are true of the
 * units themselves: 1 KG is 1000 G and 2.21 LBS on every row in the catalogue,
 * whether or not anybody typed it.
 *
 * Taken from `packConversion`'s three tables — the existing owner of these
 * constants, and the same set `unitConversions.SIZE_UNITS` is built from. A
 * fourth private copy of the pound is exactly the duplication CLAUDE.md's golden
 * rule names.
 *
 * Each table is a star around its base unit (KG, L, MTR), so mass connects to
 * mass and volume to volume and NEITHER connects to the other here. The ounce
 * alone reaches litres, as a fallback walk (`ounceBridgeGraph`): at the litres the
 * item states for one ounce, else by water. A gallon is
 * four litres (the owner's, 2026-09-14 — `LITRES_PER_GALLON`), so a row stating
 * the US 3.785 is refused the way "1 KG = 3 LBS" is. PT stays absent for the
 * reason `LITRE_PER_UNIT`'s header gives — a US and an imperial pint differ by
 * 20% and guessing is a pricing error.
 *
 * Two things follow, and both are the point:
 *   - An item stating "1 PCS = 300 G" is priceable per KG and per LB without
 *     anyone typing a mass table into an item form.
 *   - A row stating "1 KG = 3 LBS" is REFUSED, and the refusal names the figure
 *     the system actually converts by. That figure is USC's stated 2.21 (D62.1,
 *     2026-09-24; it was 2.2 from 2026-09-07), not the avoirdupois 2.204623 —
 *     see `LB_PER_KG`'s header. **A row stating "1 KG = 2.21 LBS" is therefore
 *     ACCEPTED** (as `redundant`, since the graph already implies it), and a row
 *     still stating the old 2.2 is now refused.
 *
 * The equality test needs no tolerance band and did not get one: `round6` is
 * exact at six decimals, `LB_PER_KG` is written as `1 / 2.21` precisely so that
 * `1 / LB_PER_KG === 2.21` exactly (see its header), and 2.21 is exact at six
 * decimals. A tolerance here would silently admit a genuinely wrong row. The
 * scaled branch `quantitiesAgree` takes above `ROUND6_MAX_MAGNITUDE` is not one
 * either — it is the same exact grid, applied at a magnitude where six decimals
 * still fit in a double, and no row on this scale reaches it anyway.
 */
const MEASURE_STARS: Array<[string, Record<string, number>]> = [
  ['KG', KG_PER_UNIT],
  ['L', LITRE_PER_UNIT],
  ['MTR', METRE_PER_UNIT],
];

/**
 * The undirected graph an item's conversions describe.
 *
 * `Map<unit, Map<neighbour, { ratio, row }>>`, where `ratio` answers "how many
 * NEIGHBOUR in one UNIT" — the same direction `unitConversions.conversionRatio`
 * answers in, so the two modules never disagree about which way a ratio points.
 * Both directions of every row are stored, so a walk never has to know which
 * side of a row it approached from.
 *
 * The measure stars above are laid down FIRST and a stated row overwrites one it
 * contradicts. That ordering is deliberate: this function draws what the data
 * says, it does not police it. `validateConversionList` is what stops a row
 * contradicting physics from ever being stored, and a row that reached storage
 * before this rule existed must still open rather than crash the item.
 *
 * Two rows stating the same pair keeps the LAST one, for the same reason.
 */
export function buildGraph(conversions: unknown): ConversionGraph {
  const graph: ConversionGraph = new Map();
  const link = (a: string, b: string, ratio: number, row: UnitConversion) => {
    if (!graph.has(a)) graph.set(a, new Map());
    (graph.get(a) as Map<string, { ratio: number; row: UnitConversion }>).set(b, { ratio, row });
  };

  for (const [hub, table] of MEASURE_STARS) {
    for (const unit of Object.keys(table)) {
      const perUnit = table[unit]; // how many `hub` in one `unit`
      if (unit === hub || !Number.isFinite(perUnit) || perUnit <= 0) continue;
      const row: UnitConversion = {
        fromQty: 1,
        fromUnit: unit,
        toQty: perUnit,
        toUnit: hub,
        source: 'derived',
      };
      link(unit, hub, perUnit, row);
      link(hub, unit, 1 / perUnit, row);
    }
  }

  for (const row of normalizeConversions(conversions)) {
    const from = canonicalUnitName(row.fromUnit);
    const to = canonicalUnitName(row.toUnit);
    if (!from || !to || from === to) continue; // self-loops are not edges — see the header
    const ratio = conversionRatio(row); // how many `to` in one `from`
    if (!Number.isFinite(ratio) || ratio <= 0) continue;
    link(from, to, ratio, row);
    link(to, from, 1 / ratio, row);
  }
  return graph;
}

/**
 * `graph` without KG, G, MG and LBS, plus the bridge 1 OZ = `ounceLitres(fact)` L (D170.4): a
 * walk over it crosses from a stated ounce to volume but never from another weight, so
 * KG -> OZ -> L stays closed. Only ever walked as a fallback; rows are never checked against it.
 */
function ounceBridgeGraph(graph: ConversionGraph, fact: { litrePerOunce?: unknown }): ConversionGraph {
  const out: ConversionGraph = new Map();
  graph.forEach((edges, unit) => {
    if (weighsButNotOunce(unit)) return;
    const kept = new Map<string, { ratio: number; row: UnitConversion }>();
    edges.forEach((edge, next) => {
      if (!weighsButNotOunce(next)) kept.set(next, edge);
    });
    out.set(unit, kept);
  });
  const litres = ounceLitres(fact);
  const row: UnitConversion = { fromQty: 1, fromUnit: 'OZ', toQty: litres, toUnit: 'L', source: 'derived' };
  (out.get('OZ') as Map<string, { ratio: number; row: UnitConversion }>).set('L', { ratio: litres, row });
  (out.get('L') as Map<string, { ratio: number; row: UnitConversion }>).set('OZ', { ratio: 1 / litres, row });
  return out;
}

/**
 * Every unit reachable from `startUnit`, in ONE breadth-first walk.
 *
 * `Map<unit, { ratio, steps, rows }>` where `ratio` is how many of that unit are
 * in one `startUnit`. The start unit itself is included at ratio 1, because "the
 * price in the unit it is already priced in" is a real answer a caller needs and
 * making every caller special-case it is how one of them forgets.
 *
 * One walk rather than a call per unit is the point: pricing an item at every
 * unit it states re-derives each shared prefix exactly once, and every ratio is
 * the product of RAW stored ratios rather than of any previously returned
 * answer. Breadth-first, so the path found is the shortest one and the fewest
 * multiplications carry the least float dust.
 *
 * `ounceByWater` adds, after it, the units only the ounce bridge reaches (`ounceBridgeGraph`, at
 * the item's `litrePerOunce`): a stated path always wins. Pricing asks for it; checking rows and
 * catalogue matching do not.
 */
export function reachableUnits(
  graph: ConversionGraph,
  startUnit: unknown,
  { ounceByWater = false, litrePerOunce = null }: OunceOptions = {}
): Map<string, DerivedRatio> {
  const out = new Map<string, DerivedRatio>();
  const start = canonicalUnitName(startUnit);
  if (!start) return out;

  out.set(start, { ratio: 1, steps: [], rows: [] });
  const queue: string[] = [start];
  while (queue.length) {
    const unit = queue.shift() as string;
    const here = out.get(unit) as DerivedRatio;
    const edges = graph.get(unit);
    if (!edges) continue;
    // `forEach` rather than `for...of` over the Map: the client compiles to ES5,
    // where iterating a Map directly needs --downlevelIteration. Kept the same
    // shape on both sides so the twins stay line-for-line comparable.
    edges.forEach((edge, next) => {
      if (out.has(next)) return; // first arrival is the shortest path
      out.set(next, {
        ratio: here.ratio * edge.ratio,
        steps: here.steps.concat([{ fromUnit: unit, toUnit: next, ratio: edge.ratio }]),
        rows: here.rows.concat([edge.row]),
      });
      queue.push(next);
    });
  }
  if (ounceByWater) {
    reachableUnits(ounceBridgeGraph(graph, { litrePerOunce }), start).forEach((entry, unit) => {
      if (!out.has(unit)) out.set(unit, entry);
    });
  }
  return out;
}

/**
 * The ratio from `fromUnit` to `toUnit` through the graph — how many `toUnit` in
 * one `fromUnit` — with the path that produced it, or null when the two are not
 * connected.
 *
 * Null is a REFUSAL, not a zero and not a one. Two units an item states no path
 * between cannot be converted, and guessing at a factor is how a case gets
 * quoted at the price of a piece.
 */
export function ratioBetween(
  graph: ConversionGraph,
  fromUnit: unknown,
  toUnit: unknown,
  options?: OunceOptions
): DerivedRatio | null {
  const from = canonicalUnitName(fromUnit);
  const to = canonicalUnitName(toUnit);
  if (!from || !to) return null;
  if (from === to) return { ratio: 1, steps: [], rows: [] };
  const found = reachableUnits(graph, from, options).get(to);
  return found || null;
}

/**
 * `ratioBetween` for a caller holding a raw conversion list and two raw unit
 * strings — the shape `pricingBasis` and the item form both have. Returns the
 * bare number, or null. A pricing answer, so the ounce reaches volume at the item's
 * `litrePerOunce`, else by water (D170.4).
 */
export function deriveRatio(
  conversions: unknown,
  fromUnit: unknown,
  toUnit: unknown,
  { litrePerOunce = null }: { litrePerOunce?: number | null } = {}
): number | null {
  const found = ratioBetween(buildGraph(conversions), fromUnit, toUnit, { ounceByWater: true, litrePerOunce });
  return found ? found.ratio : null;
}

/** `to` in one `from` by the rows' own stated walk, never by water; null when they state no path. */
export function statedRatio(conversions: unknown, fromUnit: unknown, toUnit: unknown): number | null {
  const found = ratioBetween(buildGraph(conversions), fromUnit, toUnit);
  return found && found.steps.length > 0 && found.ratio > 0 ? found.ratio : null;
}

/**
 * Litres in one ounce of THIS item as it states them, never by water, or null (D170.4): the rows'
 * own walk, else with each of `sizeRows` beside them in turn (its Unit Size as "1 <unit> = <size>",
 * best first).
 */
export function statedLitrePerOunce(conversions: unknown, sizeRows: unknown): number | null {
  const rows = normalizeConversions(conversions);
  for (const extra of [[] as UnitConversion[]].concat(normalizeConversions(sizeRows).map((r) => [r]))) {
    const found = statedRatio(rows.concat(extra), 'OZ', 'L');
    if (found != null) return found;
  }
  return null;
}

/** A path as the sentence a refusal quotes back: "1 CASE = 10 PCS, 1 PCS = 20 KG". */
export function describePath(steps: ConversionStep[]): string {
  return steps
    .map((s) => `1 ${s.fromUnit} = ${formatQty(s.ratio)} ${s.toUnit}`)
    .join(', ');
}

/**
 * Does `candidateRow` agree with everything `existingConversions` already
 * states?
 *
 *   no path between its two units yet — `{ ok: true, redundant: false }`. Nothing
 *                                       to contradict; this is how the graph
 *                                       grows.
 *   a path that agrees to six decimals — `{ ok: true, redundant: true }`. True,
 *                                       and already implied by what is there.
 *   a path that disagrees              — `{ ok: false }` carrying the value the
 *                                        existing rows derive, the path that
 *                                        derived it, and the sentence to show.
 *
 * `redundant` is what lets the vendor sync tell "this invoice states something
 * new" from "this invoice restates what we already knew" — without it every
 * re-sync of an unchanged invoice would grow the stored array by its own length
 * until it hit the cap. A hand-typed list is allowed to restate itself; it is
 * consistent, which is all the item form asks.
 *
 * A row that states no conversion at all (`normalizeConversion` returns null) is
 * `ok` here: whether a row is structurally complete is the validator's question,
 * already answered before this is called, and answering it twice in two places
 * is how the two answers drift.
 */
export function checkRowAgainstGraph(existingConversions: unknown, candidateRow: unknown): RowVerdict {
  const row = normalizeConversion(candidateRow);
  if (!row) return { ok: true, redundant: false };

  const from = canonicalUnitName(row.fromUnit);
  const to = canonicalUnitName(row.toUnit);
  // A self-loop states a pack fact, not an equivalence — there is nothing for it
  // to contradict, and nothing already implies it either.
  if (!from || !to || from === to) return { ok: true, redundant: false };

  const found = ratioBetween(buildGraph(existingConversions), from, to);
  if (!found) return { ok: true, redundant: false };

  // The existing rows say one `from` is `found.ratio` of `to`; this row says one
  // `from` is `row.toQty / row.fromQty` of `to`. Compare the two as the row
  // states them, so the number in the message is the number in the input box.
  const expectedToQty = found.ratio * row.fromQty;
  if (quantitiesAgree(expectedToQty, row.toQty)) return { ok: true, redundant: true };

  const path = describePath(found.steps);
  return {
    ok: false,
    fromQty: row.fromQty,
    fromUnit: from,
    toUnit: to,
    statedToQty: row.toQty,
    expectedToQty: round6(expectedToQty),
    path,
    message:
      `${formatQty(row.fromQty)} ${from} should equal ` +
      `${formatQty(round6(expectedToQty))} ${to} based on existing rows ` +
      `(${path}), not ${formatQty(row.toQty)} ${to}`,
  };
}

/**
 * Is a whole list self-consistent? `{ ok: true }`, or the FIRST row that
 * contradicts the rows before it, by its index in the list as given.
 *
 * ── Why one BFS per row is the whole check ────────────────────────────────────
 * This folds `checkRowAgainstGraph` over the list in order, accepting each row
 * into the graph before checking the next. That looks like it only tests one
 * path per row when a pair may be connected by many — but it tests all of them,
 * by induction: every edge already in the graph agreed with every path that
 * existed when it was added, so any two paths between two already-connected
 * nodes already agree. A new row can therefore only disagree with the whole
 * graph or with none of it, and one path is a witness for all of them.
 *
 * That argument is the reason this is O(rows x graph) and not exponential. Do
 * not "complete" it by enumerating every path — it is not incomplete, and the
 * enumeration has no bound.
 *
 * The index is the index in the array as PASSED IN, not in the normalized list,
 * so a caller pointing at the row a person typed points at the right one even
 * when an earlier row states nothing.
 */
export function validateConversionList(conversions: unknown): RowVerdict {
  const list = Array.isArray(conversions) ? conversions : [];
  const accepted: UnitConversion[] = [];
  for (let index = 0; index < list.length; index += 1) {
    const row = normalizeConversion(list[index]);
    if (!row) continue; // structural validity is the caller's question — see above
    const verdict = checkRowAgainstGraph(accepted, row);
    if (!verdict.ok) return { ...verdict, index };
    accepted.push(row);
  }
  return { ok: true, redundant: false };
}
